import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DataApiService } from './data-api-service';
import { QuantApiService } from '../quant-api/quant-api.service';
import { ConfigService } from 'src/app/service/config.service';
import {
  TopEventsRequestParams,
  BullBearTopEventsRequestParams,
  SentimentEventsRequestParams,
  TopEventsResponse,
  SentimentEventsResponse,
} from '../../models/score-events.interface';

const EMPTY_TOP_EVENTS: TopEventsResponse = { top_events: [] };

@Injectable({
  providedIn: 'root',
})
export class ScoreEventsService {
  private readonly API_VERSION = 'v2';
  private readonly DEFAULT_LANG = ['en', 'de'];
  private readonly DEFAULT_FEED_TYPES = [
    'non_professional',
    'online',
    'print',
    'professional',
  ];

  constructor(
    private dataApiService: DataApiService,
    private quantApiService: QuantApiService,
    private config: ConfigService,
  ) {}

  getTopEvents(
    entity: string,
    entityType: string,
    params: TopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    const endpoint = `${entityType}/${entity}/score_ts_top_events`;
    return this.dataApiService.get<TopEventsResponse>(
      endpoint,
      this.buildTopEventsQueryParams(params),
      this.API_VERSION,
    );
  }

  getPortfolioTopEvents(
    portfolioContent: unknown[],
    params: TopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    if (!portfolioContent.length) {
      return of(EMPTY_TOP_EVENTS);
    }
    return this.dataApiService.post<TopEventsResponse>(
      'portfolio/score_ts_top_events',
      portfolioContent,
      this.buildTopEventsQueryParams(params),
      this.API_VERSION,
    );
  }

  /** Query/search context — body must be a resolved query payload (resolveQuery/resolveQueryGroup), never the raw auth query. */
  getQueryTopEvents(
    queryPayload: unknown,
    params: TopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    return this.dataApiService.post<TopEventsResponse>(
      'search_query/score_ts_top_events',
      queryPayload,
      this.buildTopEventsQueryParams(params),
      this.API_VERSION,
    );
  }

  getBullBearTopEvents(
    entity: string,
    entityType: string,
    params: BullBearTopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    const endpoint = `${entityType}/${entity}/bull_bear_score_ts_top_events`;
    return this.quantApiService.get<TopEventsResponse>(
      endpoint,
      this.buildBullBearTopEventsQueryParams(params),
      'trading/v3',
    );
  }

  getPortfolioBullBearTopEvents(
    portfolioContent: unknown[],
    params: BullBearTopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    if (!portfolioContent.length) {
      return of(EMPTY_TOP_EVENTS);
    }
    return this.quantApiService.post<TopEventsResponse>(
      'portfolio/bull_bear_score_ts_top_events',
      portfolioContent,
      this.buildBullBearTopEventsQueryParams(params),
      'trading/v3',
    );
  }

  /** Query/search context — body must be a resolved query payload (resolveQuery/resolveQueryGroup), never the raw auth query. */
  getQueryBullBearTopEvents(
    queryPayload: unknown,
    params: BullBearTopEventsRequestParams,
  ): Observable<TopEventsResponse> {
    return this.quantApiService.post<TopEventsResponse>(
      'search_query/bull_bear_score_ts_top_events',
      queryPayload,
      this.buildBullBearTopEventsQueryParams(params),
      'trading/v3',
    );
  }

  private buildTopEventsQueryParams(
    params: TopEventsRequestParams,
  ): Record<string, unknown> {
    const queryParams: Record<string, unknown> = {
      project_id: this.config.appConfig.routing.apiproj,
      from: params.dateFrom,
      to: params.dateTo,
      period: params.period,
      top_events_ranking: params.ranking,
    };
    // null → omit entirely (legacy omits lang/feed_types when not in the URL)
    if (params.lang !== null) {
      queryParams['lang'] = params.lang ?? this.DEFAULT_LANG;
    }
    if (params.feedTypes !== null) {
      queryParams['feed_types'] = params.feedTypes ?? this.DEFAULT_FEED_TYPES;
    }

    // undefined nEvents → omit so the API default applies (legacy sites without `with_all_events`)
    if (params.nEvents !== undefined) {
      queryParams['n_events'] = params.nEvents;
    }

    if (params.eventWeights && Object.keys(params.eventWeights).length) {
      Object.assign(queryParams, params.eventWeights);
    } else if (params.scoreType) {
      queryParams['score_type'] = params.scoreType;
    }

    if (params.withBlackSwan) {
      queryParams['with_black_swan'] = true;
    }
    if (params.disableIndustryMateriality) {
      queryParams['disable_industry_materiality'] = true;
    }
    if (params.topEventsSign) {
      queryParams['top_events_sign'] = params.topEventsSign;
    }
    if (params.riskSensitivity !== undefined) {
      queryParams['risk_sensitivity'] = params.riskSensitivity;
    }
    if (params.extraFilters) {
      Object.assign(queryParams, params.extraFilters);
    }

    return queryParams;
  }

  private buildBullBearTopEventsQueryParams(
    params: BullBearTopEventsRequestParams,
  ): Record<string, unknown> {
    const queryParams: Record<string, unknown> = {
      project_id: this.config.appConfig.routing.apiproj,
      from: params.dateFrom,
      to: params.dateTo,
      period: params.period,
      top_events_ranking: params.ranking,
    };
    if (params.timeHorizon !== undefined) {
      queryParams['time_horizon'] = params.timeHorizon;
    }
    if (params.lang !== null) {
      queryParams['lang'] = params.lang ?? this.DEFAULT_LANG;
    }
    if (params.feedTypes !== null) {
      queryParams['feed_types'] = params.feedTypes ?? this.DEFAULT_FEED_TYPES;
    }
    if (params.nEvents !== undefined) {
      queryParams['n_events'] = params.nEvents;
    }
    if (params.topEventsSign) {
      queryParams['top_events_sign'] = params.topEventsSign;
    }
    if (params.extraFilters) {
      Object.assign(queryParams, params.extraFilters);
    }
    return queryParams;
  }

  getSentimentEvents(
    entity: string,
    entityType: string,
    params: SentimentEventsRequestParams,
  ): Observable<SentimentEventsResponse> {
    const endpoint = `${entityType}/${entity}/events`;
    const queryParams: Record<string, unknown> = {
      project_id: this.config.appConfig.routing.apiproj,
      from: params.dateFrom,
      to: params.dateTo,
      event_type: 'sub_event',
      lang: params.lang ?? this.DEFAULT_LANG,
      feed_types: params.feedTypes ?? this.DEFAULT_FEED_TYPES,
    };

    return this.dataApiService.get<SentimentEventsResponse>(
      endpoint,
      queryParams,
      this.API_VERSION,
    );
  }
}
